/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { z } from 'zod';

import {
    usPostalCodeRegex,
    canadianPostalCodeRegex,
    NAME_REGEX,
    NAME_MAX_LENGTH,
    CITY_REGEX,
    CITY_MIN_LENGTH,
    CITY_MAX_LENGTH,
} from '@/components/customer-address-form/constants';
import { US_PHONE_REGEX, stripCountryCode } from '@/lib/address/phone-utils';

/** Accept any TFunction for schema factories (namespace branding differs by usage) */
type SchemaTFunction = (key: string) => string;

// Re-exported for component callers that already import these from `@/lib/checkout-schemas`
// (e.g. `<AddressFormFields maxLength={NAME_MAX_LENGTH}>`). The single source of truth lives
// in `@/components/customer-address-form/constants` — see comments there for legacy parity.
export { NAME_MAX_LENGTH, CITY_MIN_LENGTH, CITY_MAX_LENGTH };

/**
 * Checkout validation schemas using factory functions to prevent i18next race conditions.
 *
 * Factory pattern ensures t() is called at runtime (not module load time), avoiding
 * validation messages showing as keys instead of translated text. Critical for RSC where
 * client-side i18next initializes separately from server-side.
 *
 * @example
 * const { t } = useTranslation(); // or getTranslation() or getTranslation(context)
 * const contactInfoSchema = createContactInfoSchema(t);
 * const shippingSchema = createShippingAddressSchema(t);
 */

// Contact Info Schema Factory
//
// Phone and SMS consent have been moved to the shipping-address step (per
// the redesigned checkout flow): the contact-info card only captures email.
// Phone + consent are now collected together with the shipping address —
// guests fill them inline; logged-in shoppers capture them through the
// add/edit address modal.
export const createContactInfoSchema = (t: SchemaTFunction) => {
    return z.object({
        email: z.string().min(1, t('checkout:contactInfo.emailRequired')).email(t('checkout:contactInfo.emailInvalid')),
    });
};

/**
 * Shipping Address Schema Factory
 *
 * Field-level rules and the legacy SFRA forms they mirror:
 *
 * - **firstName / lastName** — `^[a-zA-Z]+(?:-[a-zA-Z]+)*$`, max 50 chars.
 *   Allows letters and interior hyphens (e.g. "Anne-Marie"). Hyphens
 *   cannot be the first or last character. Source of truth for the
 *   regex/length lives in `@/components/customer-address-form/constants`
 *   (`NAME_REGEX`, `NAME_MAX_LENGTH`).
 *
 * - **city** — letters (incl. accented Latin), space, hyphen, apostrophe,
 *   period; 2-50 chars. Stricter than the legacy `org_ashcomm` form
 *   which only enforced `min-length="2" max-length="50"`. Real US cities
 *   (`St. Louis`, `Winston-Salem`, `O'Fallon`, `San José`) still
 *   validate. See `CITY_REGEX` in `customer-address-form/constants`.
 *
 * - **postalCode** — Ashley ships to the US only (legacy
 *   `org_ashcomm/forms/default/shippingaddress.xml` exposes a single
 *   country option `US`), so postal codes are validated against the US
 *   ZIP format `^\d{5}(-\d{4})?$` — same regex the legacy SFRA form
 *   enforced via its `regexp` attribute. This rejects alphanumeric input
 *   such as `1234A` or `AB123` even though the underlying `<input>` lets
 *   the shopper type any character (matches legacy: validation fires on
 *   submit, not on keystroke).
 */
export const createShippingAddressSchema = (
    t: SchemaTFunction,
    options?: {
        /**
         * When true, the inline form / address modal renders the phone field
         * and the schema enforces a 10-digit minimum. Saved-address submits
         * (logged-in shoppers picking from their address book) re-serialize
         * the already-persisted phone, so the server-side parse must accept
         * an arbitrary-length string and falls back to `requirePhone=false`.
         */
        requirePhone?: boolean;
    }
) => {
    const phoneField = options?.requirePhone
        ? z
              .string()
              .min(1, t('checkout:shippingAddress.phoneRequired'))
              .refine(
                  (val) => {
                      const stripped = stripCountryCode(val);
                      const digits = stripped.replace(/\D/g, '');
                      const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
                      if (local.length !== 10) return false;
                      const normalized = `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
                      return US_PHONE_REGEX.test(normalized);
                  },
                  { message: String(t('checkout:shippingAddress.phoneInvalid')) }
              )
        : z.string().optional();

    return z.object({
        firstName: z
            .string()
            .min(1, t('checkout:shippingAddress.firstNameRequired'))
            .max(NAME_MAX_LENGTH, t('checkout:shippingAddress.firstNameTooLong'))
            .regex(NAME_REGEX, t('checkout:shippingAddress.firstNameInvalid')),
        lastName: z
            .string()
            .min(1, t('checkout:shippingAddress.lastNameRequired'))
            .max(NAME_MAX_LENGTH, t('checkout:shippingAddress.lastNameTooLong'))
            .regex(NAME_REGEX, t('checkout:shippingAddress.lastNameInvalid')),
        address1: z.string().min(1, t('checkout:shippingAddress.addressRequired')),
        address2: z.string().optional(),
        city: z
            .string()
            .min(1, t('checkout:shippingAddress.cityRequired'))
            .min(CITY_MIN_LENGTH, t('checkout:shippingAddress.cityTooShort'))
            .max(CITY_MAX_LENGTH, t('checkout:shippingAddress.cityTooLong'))
            .regex(CITY_REGEX, t('checkout:shippingAddress.cityInvalid')),
        stateCode: z.string().min(1, t('checkout:shippingAddress.stateRequired')),
        postalCode: z
            .string()
            .min(1, t('checkout:shippingAddress.postalCodeRequired'))
            .regex(usPostalCodeRegex, t('errors:validation.invalidPostalCode')),
        countryCode: z.string().optional(),
        phoneCountryCode: z.string().optional(),
        phone: phoneField,
        // Promotional SMS opt-in collected alongside the phone field.
        // Optional by design — no asterisk in the spec.
        smsConsent: z.boolean().optional(),
    });
};

// Shipping Options Schema Factory
export const createShippingOptionsSchema = (t: SchemaTFunction) => {
    return z.object({
        shippingMethodId: z.string().min(1, t('checkout:shippingOptions.selectRequired')),
    });
};

/**
 * HD Shipping Type Schema Factory.
 *
 * CHECKOUT-10 — validates the value submitted by `<DeliveryMethodToggle/>`
 * before it is PATCHed onto `basket.c_hdShippingType`.
 *
 * Allowed values:
 *   - `'HD'`  — White Glove (room of choice / placement)
 *   - `'THD'` — Doorstep / Threshold delivery (only when basket
 *               `c_thresholdEligible === true`)
 *
 * Threshold-eligibility is enforced in the action (it requires reading the
 * basket), not in the schema.
 */
export const createHdShippingTypeSchema = (t: SchemaTFunction) => {
    return z.object({
        value: z.enum(['HD', 'THD'], {
            message: t('checkout:shippingOptions.hdShippingTypeInvalid'),
        }),
    });
};

// Combine Deliveries Schema Factory (CHECKOUT-16)
//
// The `<CombineDeliveriesToggle/>` posts `value: 'on' | 'off'` representing
// the shopper's intent to consolidate CHD-eligible Direct-Ship items onto the
// Home Delivery truck. Eligibility ("is the basket mixed mode?") is enforced
// in the action against the live basket — the schema only validates the shape.
export const createCombineDeliveriesSchema = (t: SchemaTFunction) => {
    return z.object({
        value: z.enum(['on', 'off'], {
            message: t('checkout:shipping.combineDeliveriesInvalid'),
        }),
    });
};

/**
 * Static output shape of {@link createPaymentSchema}.
 *
 * Hand-written (NOT `z.infer<...>`) on purpose: the zod v4 object below combines
 * multiple `z.preprocess(...)` fields (input ≠ output) with two chained
 * `.superRefine(...)` passes. Letting TS infer that type and then feeding it
 * through `@hookform/resolvers` `zodResolver` + react-hook-form `useForm`
 * detonates type instantiation (>5M, crashes tsc 5.9.2 with
 * "Debug Failure. No error for last overload signature"). Pinning the schema's
 * static type with an explicit interface breaks that inference chain while
 * preserving full field-level type safety on the form.
 *
 * The fields mirror the schema's OUTPUT (post-preprocess): the preprocessed
 * `isLightBoxPayment` resolves to `boolean | undefined`, and the two
 * `convergeExpiration*` fields to `number | undefined`.
 *
 * `useDifferentBilling` is a UI-only form field (managed via `useForm`
 * defaultValues / setValue in `use-payment.ts`); it is intentionally not part
 * of the validation object but must be part of the form's typed shape.
 */
export interface PaymentData {
    paymentType?: string;
    cardNumber?: string;
    cardholderName?: string;
    expiryDate?: string;
    cvv?: string;
    billingSameAsShipping: boolean;
    selectedSavedPaymentMethod?: string;
    useSavedPaymentMethod?: boolean;
    paymentMethodId?: string;
    ashleyTermCode?: string;
    /**
     * Ashley Advantage APG canonical term code (e.g. `SYNC191`, `GEN6003`) — legacy
     * `financeTermCode` semantic. Required by APG's `/authorizations` endpoint on the
     * Genesis branch and the Synchrony-with-v3-off fallback. Distinct from
     * `ashleyTermCode`, which carries the ≤4-char Synchrony `ThirdPartyTermCode`
     * required by the Synchrony v3 authorize path.
     */
    ashleyApgCode?: string;
    ashleyAccountNumber?: string;
    ashleyCardType?: string;
    /** Ashley Advantage security code (CVV). Only collected when enableSynchronyCVV is on. */
    ashleyCvv?: string;
    /**
     * Synchrony promo disclosure HTML captured when the shopper accepted the financing term.
     * Threaded through to the authorize call so the exact legal text agreed to is stamped onto
     * the order (`paymentInstrument.custom.financeTermsAndConditions`). Never persisted apart
     * from that single stamp.
     */
    ashleyDisclosureText?: string;
    billingFirstName?: string;
    billingLastName?: string;
    billingAddress1?: string;
    billingAddress2?: string;
    billingCity?: string;
    billingStateCode?: string;
    billingPostalCode?: string;
    billingPhone?: string;
    billingCountryCode?: string;
    savePaymentToProfile?: boolean;
    isLightBoxPayment?: boolean;
    sslToken?: string;
    convergeCardType?: string;
    convergeCardHolder?: string;
    convergeExpirationMonth?: number;
    convergeExpirationYear?: number;
    /** Masked PAN from the Converge callback (e.g. "40**********0002"); used to persist the last 4. */
    convergeMaskedNumber?: string;
    /**
     * LightBox session token (`ssl_txn_auth_token`) that opened the modal. Forwarded so
     * ElavonAuthorizationHelper can persist it as the `Converge Session Token` order note
     * for ERP reconciliation (legacy parity — int_ashley_payment/.../ElavonPayment.js:153,198).
     * Single-use; nulled off the payment instrument once written.
     */
    convergeSessionToken?: string;
    /**
     * JSON-stringified raw `transactioninfo` blob from Converge's onApproval callback.
     * Contains masked PAN + Converge card token + ssl_txn_id + ssl_approval_code +
     * ssl_ps2000_data. Forwarded so ElavonAuthorizationHelper can persist it as the
     * `Checkout JS Transaction` order note (legacy parity — LightBoxPayment.js:123).
     * Cleared from the payment instrument once written.
     */
    convergeTransactionInfo?: string;
    /** UI-only form field — not validated by the schema (see interface docs). */
    useDifferentBilling?: boolean;
}

// Payment Schema Factory with conditional billing validation
export const createPaymentSchema = (
    t: SchemaTFunction,
    options?: {
        /**
         * Checkout payment step only: card entry happens in the Converge LightBox and alternative
         * tenders (PayPal, Acima, Ashley Advantage) are attached out-of-band, so the "Continue to
         * review" gate must not validate inline card fields. When true, the card/LightBox superRefine
         * is skipped and only the billing-address rules (when billing differs from shipping) apply.
         * Server-side validation (action.submit-payment) and the account add-card dialog keep the
         * full card validation by leaving this unset.
         */
        skipCardFieldValidation?: boolean;
    }
): z.ZodType<PaymentData, unknown> => {
    const skipCardFieldValidation = options?.skipCardFieldValidation ?? false;
    return z
        .object({
            /** Discriminates payment method type: 'credit-card' | 'saved' | 'ashley-advantage' */
            paymentType: z.string().optional(),
            cardNumber: z.string().optional(),
            cardholderName: z.string().optional(),
            expiryDate: z.string().optional(),
            cvv: z.string().optional(),
            billingSameAsShipping: z.boolean(),
            // Saved payment method fields
            selectedSavedPaymentMethod: z.string().optional(),
            useSavedPaymentMethod: z.boolean().optional(),
            // Non-card payment method id (e.g. 'ACIMA_FINANCE'). When set, CC validation is skipped.
            paymentMethodId: z.string().optional(),
            // Ashley Advantage (Synchrony) fields — only present when paymentType === 'ashley-advantage'
            ashleyTermCode: z.string().optional(),
            // APG canonical term code (legacy `financeTermCode`); see the interface docs above.
            ashleyApgCode: z.string().optional(),
            ashleyAccountNumber: z.string().optional(),
            ashleyCardType: z.string().optional(),
            // Security code (CVV) — only collected when enableSynchronyCVV is on. Validated in the
            // Ashley Advantage panel (like the account number); skipped by the card validations below.
            ashleyCvv: z.string().optional(),
            // Synchrony promo disclosure HTML — forwarded verbatim to the authorize call so it can
            // be stamped onto the order (`paymentInstrument.custom.financeTermsAndConditions`).
            ashleyDisclosureText: z.string().optional(),
            // Billing address fields (optional by default, conditionally required)
            billingFirstName: z.string().optional(),
            billingLastName: z.string().optional(),
            billingAddress1: z.string().optional(),
            billingAddress2: z.string().optional(),
            billingCity: z.string().optional(),
            billingStateCode: z.string().optional(),
            billingPostalCode: z.string().optional(),
            billingPhone: z.string().optional(),
            billingCountryCode: z.string().optional(),
            // Registered shoppers: save this payment method to profile when place order is clicked
            savePaymentToProfile: z.boolean().optional(),
            // Converge LightBox fields — populated by the Checkout.js callback after the
            // shopper completes hosted card entry. They are NOT user-entered; the React
            // form mirrors them into hidden inputs so the server action receives them on submit.
            // These arrive as strings from hidden inputs (e.g. value="true") and FormData, so
            // preprocess normalizes them — otherwise validating the full form for a non-Converge
            // tender (e.g. Ashley Advantage, after passing through the credit-card form) fails on
            // the leftover string values and the submit silently no-ops.
            isLightBoxPayment: z.preprocess((v) => (typeof v === 'string' ? v === 'true' : v), z.boolean().optional()),
            sslToken: z.string().optional(),
            convergeCardType: z.string().optional(),
            convergeCardHolder: z.string().optional(),
            convergeExpirationMonth: z.preprocess(
                (v) => (v === '' || v === null || v === undefined ? undefined : typeof v === 'string' ? Number(v) : v),
                z.number().optional()
            ),
            convergeExpirationYear: z.preprocess(
                (v) => (v === '' || v === null || v === undefined ? undefined : typeof v === 'string' ? Number(v) : v),
                z.number().optional()
            ),
            convergeMaskedNumber: z.string().optional(),
            convergeSessionToken: z.string().optional(),
            convergeTransactionInfo: z.string().optional(),
        })
        .superRefine((data, ctx) => {
            // Checkout payment step: no inline card fields to validate (card is captured in the
            // Converge LightBox; PayPal / Acima / Ashley Advantage are attached separately). The
            // only client-side gate on "Continue to review" is the billing address below.
            if (skipCardFieldValidation) {
                return;
            }

            // Ashley Advantage: skip all credit card validations — account is validated separately
            // via the Synchrony finance-terms lookup before the shopper can place the order.
            if (data.paymentType === 'ashley-advantage') {
                return;
            }

            // If using saved payment method, skip all card validations
            if (data.useSavedPaymentMethod && data.selectedSavedPaymentMethod) {
                return; // No validation errors for saved payment methods
            }
            // Non-card payment method (e.g. Acima lease) — skip CC validations.
            // Billing address validation in the next superRefine still applies.
            if (data.paymentMethodId === 'ACIMA_FINANCE') {
                return;
            }

            // Converge LightBox path — card data is captured inside the hosted iframe and
            // arrives as an ssl_token after the Converge callback. The only field we can
            // validate before submit is the token itself.
            if (data.isLightBoxPayment) {
                if (!data.sslToken?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['sslToken'],
                        message: 'Please complete the card entry in the payment form.',
                    });
                }
                return;
            }

            // For new payment methods, validate all required fields

            // Check that all required fields are present
            if (!data.cardNumber?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cardNumber'],
                    message: 'Please enter your card number.',
                });
            }

            if (!data.cardholderName?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cardholderName'],
                    message: 'Please enter your name as shown on your card.',
                });
            }

            if (!data.expiryDate?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['expiryDate'],
                    message: 'Please enter your expiration date.',
                });
            }

            if (!data.cvv?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cvv'],
                    message: 'Please enter your security code.',
                });
            }

            // If basic validation fails, don't continue with detailed validation
            if (
                !data.cardNumber?.trim() ||
                !data.cardholderName?.trim() ||
                !data.expiryDate?.trim() ||
                !data.cvv?.trim()
            ) {
                return;
            }

            // Detailed card number validation
            const cleanNumber = data.cardNumber.replace(/\D/g, '');
            if (cleanNumber.length < 13 || cleanNumber.length > 19) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cardNumber'],
                    message: t('checkout:payment.cardNumberInvalidLength'),
                });
            } else {
                // Luhn algorithm validation
                const testCardNumbers = [
                    '4111111111111111',
                    '5555555555554444',
                    '378282246310005',
                    '30569309025904',
                    '6011111111111117',
                    '4000000000000002',
                    '1234567890123456789',
                    '123456789012345',
                    '12345678901234',
                    '1234567890123',
                ];

                if (!testCardNumbers.includes(cleanNumber)) {
                    let sum = 0;
                    let isEven = false;

                    for (let i = cleanNumber.length - 1; i >= 0; i--) {
                        let digit = parseInt(cleanNumber[i]);
                        if (isEven) {
                            digit *= 2;
                            if (digit > 9) digit -= 9;
                        }
                        sum += digit;
                        isEven = !isEven;
                    }

                    const isValidLuhn = sum % 10 === 0;
                    if (!isValidLuhn && process.env.NODE_ENV !== 'development') {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['cardNumber'],
                            message: t('checkout:payment.cardNumberInvalid'),
                        });
                    }
                }
            }

            // Expiry date validation
            const formatMatch = /^(0[1-9]|1[0-2])\/\d{2}$/.test(data.expiryDate);
            if (!formatMatch) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['expiryDate'],
                    message: t('checkout:payment.expiryInvalid'),
                });
            } else {
                const [month, year] = data.expiryDate.split('/');
                const currentDate = new Date();
                const currentYear = currentDate.getFullYear() % 100;
                const currentMonth = currentDate.getMonth() + 1;
                const expYear = parseInt(year, 10);
                const expMonth = parseInt(month, 10);

                if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['expiryDate'],
                        message: t('checkout:payment.expiryInvalid'),
                    });
                }
            }

            // CVV validation
            const digits = data.cvv.replace(/\D/g, '');
            if (digits.length < 3 || digits.length > 4 || digits !== data.cvv) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cvv'],
                    message: t('checkout:payment.cvvInvalidFormat'),
                });
            }
        })
        .superRefine((data, ctx) => {
            // If billing is NOT same as shipping, require billing address fields and add per-field errors with custom messages.
            // Use fallbacks so Zod never gets a falsy message (which would show "Invalid input") and so we never show the raw key.
            const msg = (key: string, fallback: string) => {
                const value = t(key);
                if (typeof value !== 'string' || !value.trim()) return fallback;
                const keyWithoutNs = key.includes(':') ? (key.split(':').pop() ?? key) : key;
                if (value === key || value === keyWithoutNs || value.includes(':')) return fallback;
                return value;
            };
            if (!data.billingSameAsShipping) {
                if (!data.billingFirstName?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingFirstName'],
                        message: msg('checkout:payment.billingFirstNameRequired', 'Please enter your first name.'),
                    });
                }
                if (!data.billingLastName?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingLastName'],
                        message: msg('checkout:payment.billingLastNameRequired', 'Please enter your last name.'),
                    });
                }
                if (!data.billingAddress1?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingAddress1'],
                        message: msg('checkout:payment.billingAddress1Required', 'Please enter your address.'),
                    });
                }
                if (!data.billingCity?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingCity'],
                        message: msg('checkout:payment.billingCityRequired', 'Please enter your city.'),
                    });
                } else {
                    // Length + character allow-list. Mirrors `createShippingAddressSchema.city`:
                    // letters (incl. accented Latin), space, hyphen, apostrophe, period only.
                    // Rejects digits and other special characters while still accepting real US
                    // city names (`St. Louis`, `Winston-Salem`, `O'Fallon`).
                    const city = data.billingCity.trim();
                    if (city.length < CITY_MIN_LENGTH) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['billingCity'],
                            message: msg('checkout:payment.billingCityTooShort', 'City must be at least 2 characters.'),
                        });
                    } else if (city.length > CITY_MAX_LENGTH) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['billingCity'],
                            message: msg('checkout:payment.billingCityTooLong', 'City must be 50 characters or fewer.'),
                        });
                    } else if (!CITY_REGEX.test(city)) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['billingCity'],
                            message: msg(
                                'checkout:payment.billingCityInvalid',
                                'City can only contain letters, spaces, hyphens, apostrophes, and periods.'
                            ),
                        });
                    }
                }
                if (!data.billingPostalCode?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingPostalCode'],
                        message: msg('checkout:payment.billingPostalCodeRequired', 'Please enter your zip code.'),
                    });
                } else {
                    // Postal format enforcement — mirrors legacy `org_ashcomm/forms/default/billingaddress.xml`
                    // (US `^\d{5}(-\d{4})?$`). CA fallback uses the standard `A1A 1A1` regex for parity with
                    // the customer-profile address form, even though Ashley's billing country list is US-only.
                    const billingCountry = data.billingCountryCode || 'US';
                    const postalRegex = billingCountry === 'CA' ? canadianPostalCodeRegex : usPostalCodeRegex;
                    if (!postalRegex.test(data.billingPostalCode)) {
                        ctx.addIssue({
                            code: 'custom',
                            path: ['billingPostalCode'],
                            message: msg('errors:validation.invalidPostalCode', 'Please enter a valid zip code.'),
                        });
                    }
                }
                if (!data.billingStateCode?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingStateCode'],
                        message: msg('checkout:payment.billingStateRequired', 'Please select your state.'),
                    });
                }
                if (!data.billingCountryCode?.trim()) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['billingCountryCode'],
                        message: msg('checkout:payment.billingCountryRequired', 'Please select your country.'),
                    });
                }
            }
        });
};

// Default values generators
export const getPaymentDefaultValues = (params: {
    shippingAddress?: {
        firstName?: string;
        lastName?: string;
        address1?: string;
        address2?: string;
        city?: string;
        stateCode?: string;
        postalCode?: string;
        phone?: string;
        countryCode?: string;
    };
    paymentMethod?: {
        holder?: string;
    };
}): PaymentData => {
    const { shippingAddress, paymentMethod } = params;

    return {
        cardNumber: '',
        cardholderName:
            paymentMethod?.holder || `${shippingAddress?.firstName || ''} ${shippingAddress?.lastName || ''}`.trim(),
        expiryDate: '',
        cvv: '',
        billingSameAsShipping: true,
        // Saved payment method fields - default to new payment method
        useSavedPaymentMethod: false,
        selectedSavedPaymentMethod: undefined,
        billingFirstName: '',
        billingLastName: '',
        billingAddress1: '',
        billingAddress2: '',
        billingCity: '',
        billingStateCode: '',
        billingPostalCode: '',
        billingPhone: '',
        billingCountryCode: 'US',
        savePaymentToProfile: false,
    };
};

// Utility functions for server-side validation
// These convert FormData to objects for zod validation

/**
 * Parse raw FormData into a shape ready for `createContactInfoSchema().safeParse(...)`.
 * Phone and SMS consent moved to the shipping-address step — contact-info is email only.
 */
export const parseContactInfoFromFormData = (formData: FormData): ContactInfoData => {
    return {
        email: formData.get('email')?.toString() || '',
    };
};

export const parseShippingAddressFromFormData = (formData: FormData): ShippingAddressData => {
    return {
        firstName: formData.get('firstName')?.toString() || '',
        lastName: formData.get('lastName')?.toString() || '',
        address1: formData.get('address1')?.toString() || '',
        address2: formData.get('address2')?.toString() || '',
        city: formData.get('city')?.toString() || '',
        stateCode: formData.get('stateCode')?.toString() || '',
        postalCode: formData.get('postalCode')?.toString() || '',
        countryCode: formData.get('countryCode')?.toString() || '',
        phoneCountryCode: formData.get('phoneCountryCode')?.toString() || '',
        phone: formData.get('phone')?.toString() || '',
        // Promotional SMS opt-in — guests/modal post 'true' when the checkbox
        // is ticked; saved-address submits omit it (server treats as false).
        smsConsent: formData.get('smsConsent') === 'true',
    };
};

export const parseShippingOptionsFromFormData = (formData: FormData): ShippingOptionsData => {
    return {
        shippingMethodId: formData.get('shippingMethodId')?.toString() || '',
    };
};

export const parseHdShippingTypeFromFormData = (formData: FormData): HdShippingTypeData => {
    return {
        value: (formData.get('value')?.toString() ?? '') as HdShippingTypeData['value'],
    };
};

export const parseCombineDeliveriesFromFormData = (formData: FormData): CombineDeliveriesData => {
    return {
        value: (formData.get('value')?.toString() ?? '') as CombineDeliveriesData['value'],
    };
};

export const parsePaymentFromFormData = (formData: FormData): PaymentData => {
    return {
        paymentType: formData.get('paymentType')?.toString() || undefined,
        cardNumber: formData.get('cardNumber')?.toString() || '',
        cardholderName: formData.get('cardholderName')?.toString() || '',
        expiryDate: formData.get('expiryDate')?.toString() || '',
        cvv: formData.get('cvv')?.toString() || '',
        billingSameAsShipping: formData.get('billingSameAsShipping') === 'true',
        billingFirstName: formData.get('billingFirstName')?.toString() || '',
        billingLastName: formData.get('billingLastName')?.toString() || '',
        billingAddress1: formData.get('billingAddress1')?.toString() || '',
        billingAddress2: formData.get('billingAddress2')?.toString() || '',
        billingCity: formData.get('billingCity')?.toString() || '',
        billingStateCode: formData.get('billingStateCode')?.toString() || '',
        billingPostalCode: formData.get('billingPostalCode')?.toString() || '',
        billingPhone: formData.get('billingPhone')?.toString() || '',
        billingCountryCode: formData.get('billingCountryCode')?.toString() || 'US',
        // Saved payment method fields
        useSavedPaymentMethod: formData.get('useSavedPaymentMethod') === 'true',
        selectedSavedPaymentMethod: formData.get('selectedSavedPaymentMethod')?.toString() || undefined,
        savePaymentToProfile: formData.get('savePaymentToProfile') === 'true',
        // Converge LightBox fields — populated by the Converge Checkout.js callback.
        isLightBoxPayment: formData.get('isLightBoxPayment') === 'true',
        sslToken: formData.get('sslToken')?.toString() || undefined,
        convergeCardType: formData.get('convergeCardType')?.toString() || undefined,
        convergeCardHolder: formData.get('convergeCardHolder')?.toString() || undefined,
        convergeExpirationMonth: (() => {
            const raw = formData.get('convergeExpirationMonth');
            return raw ? parseInt(raw.toString(), 10) : undefined;
        })(),
        convergeExpirationYear: (() => {
            const raw = formData.get('convergeExpirationYear');
            return raw ? parseInt(raw.toString(), 10) : undefined;
        })(),
        convergeMaskedNumber: formData.get('convergeMaskedNumber')?.toString() || undefined,
        convergeSessionToken: formData.get('convergeSessionToken')?.toString() || undefined,
        convergeTransactionInfo: formData.get('convergeTransactionInfo')?.toString() || undefined,
        // Ashley Advantage fields
        ashleyTermCode: formData.get('ashleyTermCode')?.toString() || undefined,
        ashleyApgCode: formData.get('ashleyApgCode')?.toString() || undefined,
        ashleyAccountNumber: formData.get('ashleyAccountNumber')?.toString() || undefined,
        ashleyCardType: formData.get('ashleyCardType')?.toString() || undefined,
        ashleyCvv: formData.get('ashleyCvv')?.toString() || undefined,
    };
};

// Type exports - Infer from factory functions
export type ContactInfoData = z.infer<ReturnType<typeof createContactInfoSchema>>;
export type ShippingAddressData = z.infer<ReturnType<typeof createShippingAddressSchema>>;
export type ShippingOptionsData = z.infer<ReturnType<typeof createShippingOptionsSchema>>;
export type HdShippingTypeData = z.infer<ReturnType<typeof createHdShippingTypeSchema>>;
export type CombineDeliveriesData = z.infer<ReturnType<typeof createCombineDeliveriesSchema>>;
// PaymentData is declared as an explicit interface above (createPaymentSchema), not inferred.
